import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FaBars,
  FaBed,
  FaClipboardList,
  FaUserNurse,
  FaProcedures,
  FaNotesMedical,
} from "react-icons/fa";
import WardAllocation from "../components/WardAllocation";
import PatientManagement from "../components/PatientManagement";
import TreatmentPlan from "../components/TreatmentPlan";

export default function IpdManagement() {
  // Persist active menu state via URL search params
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultMenu = "patientManagement";
  const activeMenu = searchParams.get("menu") || defaultMenu;

  // isCollapsed controls the static sidebar’s width on desktop.
  // isSidebarOpen will force the sidebar to appear as an overlay on any screen.
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const menuItems = [
    {
      id: "patientManagement",
      label: "Patient Management",
      icon: <FaUserNurse />,
      component: PatientManagement,
    },
    {
      id: "treatmentPlans",
      label: "Treatment Plans and Doctor Consultation",
      icon: <FaProcedures />,
      component: TreatmentPlan,
    },
    {
      id: "wardAllocation",
      label: "Ward Allocation Status",
      icon: <FaBed />,
      component: WardAllocation,
    },
    {
      id: "medicalRecords",
      label: "Medical Records",
      icon: <FaNotesMedical />,
      component: null,
    },
    {
      id: "reports",
      label: "Reports",
      icon: <FaClipboardList />,
      component: null,
    },
  ];

  const renderComponent = () => {
    const activeItem = menuItems.find((item) => item.id === activeMenu);
    return activeItem && activeItem.component ? (
      React.createElement(activeItem.component)
    ) : (
      <div className="text-center text-gray-500">No component available</div>
    );
  };

  const handleMenuClick = (id) => {
    // Update URL query parameter and close overlay (if open)
    setSearchParams({ menu: id });
    setIsSidebarOpen(false);
  };

  return (
    <div className="flex min-h-screen bg-gray-100 relative">
      {/* Sidebar container:
          - When isSidebarOpen is true, render as an overlay (fixed, full height, with z-index)
          - Otherwise, on mobile hide it and on desktop show as static with collapse support */}
      <div
        className={`transition-transform duration-300 transform bg-white shadow-md flex-shrink-0 ${
          isSidebarOpen
            ? // Overlay mode: always fixed and visible on top
              "fixed inset-y-0 left-0 z-50 w-64 translate-x-0"
            : // Static mode: hidden on mobile; on desktop, relative with collapse handling
              "hidden md:block md:relative md:translate-x-0 " +
              (isCollapsed ? "w-16" : "w-64")
        }`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
          <h1
            className={`text-lg font-semibold text-gray-800 transition-opacity duration-300 ${
              isCollapsed && !isSidebarOpen ? "hidden" : "block"
            }`}
          >
            IPD Management
          </h1>
          <button
            className="text-gray-600 hover:text-gray-800"
            onClick={() => {
              // Toggle overlay mode on all screen sizes
              setIsSidebarOpen(!isSidebarOpen);
            }}
          >
            <FaBars />
          </button>
        </div>

        {/* Sidebar Menu */}
        <ul className="flex flex-col">
          {menuItems.map((item) => (
            <li
              key={item.id}
              className={`flex items-center p-3 hover:bg-blue-100 cursor-pointer ${
                activeMenu === item.id
                  ? "bg-blue-50 text-blue-700"
                  : "text-gray-600"
              }`}
              onClick={() => handleMenuClick(item.id)}
            >
              <span className="text-xl mr-3">{item.icon}</span>
              {(!isCollapsed || isSidebarOpen) && (
                <span className="flex-1">{item.label}</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Backdrop for overlay mode (always on when overlay is open) */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      {/* Content Area */}
      <div className="flex-grow bg-gray-50">
        <div className="max-w-full p-6 overflow-auto">
          {/* Toggle button to open overlay sidebar (visible only on mobile when sidebar is closed) */}
          {!isSidebarOpen && (
            <button
              className="mb-4 text-gray-600 hover:text-gray-800 md:hidden"
              onClick={() => setIsSidebarOpen(true)}
            >
              <FaBars size={24} />
            </button>
          )}
          {/* Header for Active Menu */}
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {menuItems.find((item) => item.id === activeMenu)?.label}
          </h2>
          {renderComponent()}
        </div>
      </div>
    </div>
  );
}
